/**
 * 联系我们页的腾讯地图。
 * 加载腾讯地图 JavaScript API GL，并在指定容器中标注公司位置。
 */

const SCRIPT_ID = 'tencent-map-gljs';
const SCRIPT_BASE = 'https://map.qq.com/api/gljs?v=1.exp&key=';
const MAP_ZOOM = 15;
const COMPANY_NAME = '杭州朗杰测控技术开发有限公司';
const COMPANY_ADDRESS = '浙江省杭州市拱墅区储鑫路21号网谷创新中心10幢1505室';
// 原百度坐标为 BD-09（120.167778, 30.342566）。腾讯地图使用 GCJ-02，直接沿用会偏移约数百米。
const COMPANY_LAT = 30.336771;
const COMPANY_LNG = 120.161246;

let scriptPromise = null;

/**
 * 在地图容器中显示提示。
 * 输入：container 地图容器；message 提示文案
 * 输出：清理函数，调用后移除提示
 */
function showMapMessage(container, message) {
    const tip = document.createElement('div');
    tip.textContent = message;
    tip.style.cssText = 'height:100%;display:flex;align-items:center;justify-content:center;color:#666;font-size:14px;';
    container.appendChild(tip);
    return () => {
        if (tip.parentNode === container) {
            container.removeChild(tip);
        }
    };
}

/**
 * 加载腾讯地图脚本，同一页面只插入一次。
 * 输入：mapKey 腾讯位置服务 Key
 * 输出：Promise，成功时 resolve 为 window.TMap
 */
function loadTencentMap(mapKey) {
    if (window.TMap) {
        return Promise.resolve(window.TMap);
    }
    if (scriptPromise) {
        return scriptPromise;
    }
    scriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.charset = 'utf-8';
        script.src = `${SCRIPT_BASE}${encodeURIComponent(mapKey)}`;
        script.onload = () => {
            if (window.TMap) {
                resolve(window.TMap);
                return;
            }
            scriptPromise = null;
            reject(new Error('腾讯地图脚本已加载，但未找到 TMap'));
        };
        script.onerror = () => {
            scriptPromise = null;
            script.remove();
            reject(new Error('腾讯地图脚本加载失败'));
        };
        document.body.appendChild(script);
    });
    return scriptPromise;
}

/**
 * 组装信息窗内容，保留原来的公司名和地址样式。
 * 输入：无
 * 输出：信息窗 HTML 字符串
 */
function buildInfoContent() {
    return [
        '<div style="width:280px;text-align:left;">',
        `<div style="color:#3f51b5;font-weight:bold;font-size:16px;">${COMPANY_NAME}</div>`,
        `<div style="font-size:12px;margin-top:6px;color:#333;line-height:1.5;">${COMPANY_ADDRESS}</div>`,
        '</div>',
    ].join('');
}

/**
 * 创建地图、标注和信息窗。
 * 输入：TMap 腾讯地图命名空间；container 地图容器
 * 输出：销毁函数，离开页面时释放地图实例
 */
function createCompanyMap(TMap, container) {
    const center = new TMap.LatLng(COMPANY_LAT, COMPANY_LNG);
    const map = new TMap.Map(container, {
        center,
        zoom: MAP_ZOOM,
        pitch: 0,
        rotation: 0,
        // 与原先百度地图一致，允许滚轮缩放
        scrollable: true,
    });
    const marker = new TMap.MultiMarker({
        id: 'company-marker',
        map,
        geometries: [{
            id: 'company',
            styleId: 'marker',
            position: center,
        }],
    });
    const infoWindow = new TMap.InfoWindow({
        map,
        position: center,
        offset: { x: 0, y: -32 },
        content: buildInfoContent(),
    });
    // 信息窗可关闭，点击标注后重新打开，方便再次查看地址
    marker.on('click', () => {
        infoWindow.setPosition(center);
        infoWindow.open();
    });
    return () => {
        map.destroy();
    };
}

/**
 * 渲染公司位置地图。
 * 输入：options.containerId 容器 id；options.mapKey 腾讯地图 Key
 * 输出：Promise，resolve 为销毁函数
 */
export function renderCompanyMap({ containerId, mapKey }) {
    const container = document.getElementById(containerId);
    if (!container) {
        console.warn('未找到地图容器', containerId);
        return Promise.resolve(() => {});
    }
    if (!mapKey) {
        console.warn('未配置腾讯地图 Key，联系我们页无法显示地图');
        return Promise.resolve(showMapMessage(container, '地图暂不可用，请配置腾讯地图 Key'));
    }
    return loadTencentMap(mapKey).then((TMap) => {
        const current = document.getElementById(containerId);
        if (!current) {
            return () => {};
        }
        return createCompanyMap(TMap, current);
    }).catch((error) => {
        console.error('腾讯地图初始化失败', error);
        const current = document.getElementById(containerId);
        if (!current) {
            return () => {};
        }
        return showMapMessage(current, '地图加载失败');
    });
}
